import React, { useState, useEffect } from 'react';
import {
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Input,
    Spinner,
    Badge,
    Modal,
    ModalBody,
    Table,
} from 'reactstrap';
import FeatherIconCom from '../../../Utils/CommonComponents/CommonIcons/FeatherIconCom';
import { getData, postData } from '../../../CommonElements/Functions/PostGetFun';
import { API } from '../../../api/API';
import { toast } from 'react-toastify';
import moment from 'moment';
import Select from 'react-select';
import Loader from '../../../Layout/Loader';
import { isOwnRequest } from '../../../Utils/authUtils';

export default function SupportApprovals() {
    const [selectedMonth, setSelectedMonth] = useState<string>(moment().format('MM-YYYY'));
    const [positionOptions, setPositionOptions] = useState<any[]>([]);
    const [selectedPosition, setSelectedPosition] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [cardsData, setCardsData] = useState<any[]>([]);
    const [selectedIds, setSelectedIds] = useState<any[]>([]);
    const [actionLoading, setActionLoading] = useState<boolean>(false);

    // Product Detail Modal State
    const [detailModalOpen, setDetailModalOpen] = useState<boolean>(false);
    const [detailLoading, setDetailLoading] = useState<boolean>(false);
    const [detailItems, setDetailItems] = useState<any[]>([]);
    const [selectedOutletName, setSelectedOutletName] = useState<string>('');

    const monthOptions = Array.from({ length: 6 }).map((_, i) => {
        const d = moment().subtract(i, 'months');
        return { label: d.format('MMM YYYY'), value: d.format('MM-YYYY') };
    });

    const fetchPositions = async () => {
        const serverUrl = localStorage.getItem("serverUrl");
        if (!serverUrl) return;
        try {
            const url = `${serverUrl}${API.getPositionTreeView}`;
            const response = await getData(url, true);
            const flat: any[] = [];
            const flatten = (node: any) => {
                if (!node) return;
                const empName = (node.EmployeeName || node.employee_name || node.Employee_Name || node.employeeName || node.name || '').trim();
                const posName = (node.position_name || node.positionName || '').trim();
                const label = [posName, empName].filter(Boolean).join(' - ');
                flat.push({ label: label || 'Unknown', value: String(node.position_id ?? '') });
                if (Array.isArray(node.children)) {
                    node.children.forEach((child: any) => flatten(child));
                }
            };
            const treeData = response?.data?.data || response?.data;
            if (treeData) {
                flatten(treeData);
                setPositionOptions(flat);
                const userPositionId = localStorage.getItem("positionId");
                if (userPositionId && flat.some((p: any) => String(p.value) === String(userPositionId))) {
                    setSelectedPosition(String(userPositionId));
                } else if (flat.length > 0) {
                    setSelectedPosition(flat[0].value);
                }
            }
        } catch (err) {
            console.error("Error fetching position tree:", err);
        }
    };

    const loadSupportApprovals = async (moye: string, position_id: string) => {
        const serverUrl = localStorage.getItem("serverUrl");
        if (!serverUrl || !position_id) return;
        setLoading(true);
        try {
            const url = `${serverUrl}${API.GetAllSubmittedOutlets}`;
            const response = await getData(url, true, { moye, position_id });
            const rows = Array.isArray(response?.data) ? response.data : (Array.isArray(response?.data?.data) ? response.data.data : []);

            const mapped = rows.map((item: any, index: number) => ({
                id: item?.id ?? item?.OutletOrgId ?? item?.Outlet_Id ?? (index + 1),
                outletId: item?.Outlet_Id ?? item?.outlet_id ?? null,
                outletOrgId: item?.OutletOrgId ?? item?.outlet_org_id ?? null,
                supportCaptureId: item?.SupportCaptureId ?? item?.support_capture_id ?? item?.supportCaptureId ?? null,
                supportCaptureStatus: item?.SupportCaptureStatus ?? item?.support_capture_status ?? item?.supportCaptureStatus ?? '',
                outletName: item?.OutletName ?? item?.outlet_name ?? '-',
                outletType: item?.OutlettypeName ?? item?.outlet_type ?? '-',
                outletCode: item?.OutletCode ?? item?.outlet_code ?? '-',
                beatName: item?.BeatName ?? '-',
                territoryName: item?.TerritoryName ?? '-',
                contactNo: item?.OutletContactNo ?? item?.outlet_contact_no ?? '-',
                lastSupportCaptureDate: item?.LastSupportCaptureDate ?? item?.last_support_capture_date ?? null,
                totalQuantity: Number(item?.TotalQuantity ?? item?.total_qty ?? item?.totalQty ?? 0) || 0,
                totalAmount: Number(item?.TotalAmount ?? item?.total_value ?? item?.totalValue ?? 0) || 0,
                supportCaptureMedia: item?.SupportCaptureMedia ?? item?.support_capture_media ?? '',
                CreatedBy: item?.CreatedBy ?? item?.created_by,
                CreatedByPosition: item?.CreatedByPosition ?? item?.created_by_position,
                EmployeeId: item?.EmployeeId ?? item?.employee_id,
                PositionId: item?.PositionId ?? item?.position_id ?? selectedPosition,
            }));

            setCardsData(mapped);
            setSelectedIds([]);
        } catch (err) {
            console.error("Error fetching support approvals:", err);
            setCardsData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPositions();
    }, []);

    useEffect(() => {
        if (selectedMonth && selectedPosition) {
            loadSupportApprovals(selectedMonth, selectedPosition);
        }
    }, [selectedMonth, selectedPosition]);

    const toggleSelection = (id: any) => {
        if (!id) return;
        setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    };

    const toggleSelectAll = () => {
        const validCards = cardsData.filter(item => item.supportCaptureId && !isOwnRequest(item));
        if (selectedIds.length === validCards.length && validCards.length > 0) {
            setSelectedIds([]);
        } else {
            setSelectedIds(validCards.map(item => item.supportCaptureId));
        }
    };

    const handleBulkAction = async (status: 'Approved' | 'Rejected') => {
        const serverUrl = localStorage.getItem("serverUrl");
        if (!serverUrl || selectedIds.length === 0) return;
        setActionLoading(true);
        try {
            const url = `${serverUrl}${API.ApproveRejectSupport}`;
            let hasError = false;
            for (const id of selectedIds) {
                const res = await postData(url, true, {
                    support_capture_id: id,
                    status,
                    remarks: ''
                });
                if (res.status !== 200 && res.data?.statusCode !== 200) {
                    hasError = true;
                }
            }

            if (!hasError) {
                toast.success(`Successfully ${status.toLowerCase()} all selected items`);
            } else {
                toast.warning(`Some items failed to be ${status.toLowerCase()}`);
            }
            loadSupportApprovals(selectedMonth, selectedPosition);
        } catch (err) {
            console.error("Error performing bulk action:", err);
            toast.error(`Failed to process bulk ${status.toLowerCase()}`);
        } finally {
            setActionLoading(false);
        }
    };

    const viewSupportDetails = async (item: any) => {
        const serverUrl = localStorage.getItem("serverUrl");
        if (!serverUrl || !item.supportCaptureId) return;
        setSelectedOutletName(item.outletName);
        setDetailLoading(true);
        setDetailModalOpen(true);
        try {
            const url = `${serverUrl}${API.ProductListingId}`;
            const response = await getData(url, true, { support_capture_id: item.supportCaptureId });
            if (response.status === 200 || response.data?.statusCode === 200) {
                const list = Array.isArray(response.data) ? response.data : (Array.isArray(response.data?.data) ? response.data.data : []);
                setDetailItems(list);
            } else {
                setDetailItems([]);
            }
        } catch (err) {
            console.error("Error fetching support details:", err);
            setDetailItems([]);
        } finally {
            setDetailLoading(false);
        }
    };

    const getStatusBadgeColor = (status: string) => {
        const s = (status || '').toLowerCase();
        if (s === 'submitted') return 'success';
        if (s === 'approved') return 'primary';
        if (s === 'rejected') return 'danger';
        return 'warning';
    };

    return (
        <div className="p-3" style={{ color: '#000000' }}>
            {/* Filter Toolbar */}
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3 p-3 bg-light border rounded">
                <div className="d-flex align-items-center gap-3 flex-wrap">
                    <div style={{ minWidth: '150px' }}>
                        <label className="form-label small fw-bold text-dark mb-1" style={{ color: '#000000' }}>Select Month</label>
                        <Input
                            type="select"
                            value={selectedMonth}
                            onChange={(e) => setSelectedMonth(e.target.value)}
                            className="bg-white border text-dark shadow-none fw-semibold"
                            style={{ fontSize: '0.9rem', color: '#000000' }}
                        >
                            {monthOptions.map((m, idx) => (
                                <option key={idx} value={m.value}>
                                    {m.label}
                                </option>
                            ))}
                        </Input>
                    </div>

                    <div style={{ minWidth: '320px' }}>
                        <label className="form-label small fw-bold text-dark mb-1" style={{ color: '#000000' }}>Team Member</label>
                        <Select
                            options={positionOptions}
                            value={positionOptions.find((p) => String(p.value) === String(selectedPosition)) || null}
                            onChange={(opt: any) => setSelectedPosition(opt ? String(opt.value) : '')}
                            isSearchable={true}
                            placeholder="Search Team Member..."
                            className="text-dark fw-semibold"
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    borderColor: '#dee2e6',
                                    minHeight: '38px',
                                    color: '#000000',
                                }),
                                singleValue: (base) => ({
                                    ...base,
                                    color: '#000000',
                                    fontWeight: '600',
                                }),
                                option: (base, state) => ({
                                    ...base,
                                    color: '#000000',
                                    backgroundColor: state.isFocused ? '#e9ecef' : '#ffffff',
                                }),
                            }}
                        />
                    </div>
                </div>

                {/* Bulk Actions */}
                {cardsData.length > 0 && (
                    <div className="d-flex align-items-center gap-3">
                        <div className="d-flex align-items-center gap-2 cursor-pointer" onClick={toggleSelectAll} style={{ cursor: 'pointer' }}>
                            <Input
                                type="checkbox"
                                checked={selectedIds.length === cardsData.filter(i => i.supportCaptureId).length && cardsData.length > 0}
                                onChange={toggleSelectAll}
                                className="cursor-pointer"
                            />
                            <span className="fw-bold text-dark" style={{ fontSize: '0.95rem' }}>Select All</span>
                        </div>

                        {selectedIds.length > 0 && (
                            <Button
                                color="success"
                                size="sm"
                                disabled={actionLoading}
                                onClick={() => handleBulkAction('Approved')}
                                className="fw-bold text-white px-3"
                            >
                                {actionLoading ? <Spinner size="sm" /> : `Approve Selected (${selectedIds.length})`}
                            </Button>
                        )}
                    </div>
                )}
            </div>

            {loading ? (
                <Loader height="250px" />
            ) : cardsData.length > 0 ? (
                <Row className="g-3">
                    {cardsData.map((item: any, idx: number) => {
                        const isOwn = isOwnRequest(item);
                        const isSelected = selectedIds.includes(item.supportCaptureId);

                        return (
                            <Col md={6} lg={4} key={idx}>
                                <Card className={`border shadow-sm h-100 ${isSelected ? 'border-primary bg-light-primary' : ''}`}>
                                    <CardBody className="p-3 d-flex flex-column justify-content-between" style={{ color: '#000000' }}>
                                        <div>
                                            <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                                                <div className="d-flex align-items-center gap-2">
                                                    {!isOwn && (
                                                        <Input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={() => toggleSelection(item.supportCaptureId)}
                                                            className="cursor-pointer"
                                                        />
                                                    )}
                                                    <h6 className="fw-bold text-primary m-0" style={{ fontSize: '1rem' }}>
                                                        {item.outletName}
                                                    </h6>
                                                </div>
                                                <div className="d-flex gap-1">
                                                    {item.supportCaptureStatus && (
                                                        <Badge color={getStatusBadgeColor(item.supportCaptureStatus)} className="px-2 py-1">
                                                            {item.supportCaptureStatus}
                                                        </Badge>
                                                    )}
                                                    <Badge color="info" className="px-2 py-1">
                                                        {item.outletType}
                                                    </Badge>
                                                </div>
                                            </div>

                                            <div className="small text-dark mb-1">
                                                <FeatherIconCom iconName="Tag" size={13} className="me-1 text-secondary" />
                                                <strong>Code:</strong> {item.outletCode}
                                            </div>
                                            <div className="small text-dark mb-1">
                                                <FeatherIconCom iconName="Phone" size={13} className="me-1 text-secondary" />
                                                <strong>Contact:</strong> {item.contactNo}
                                            </div>
                                            <div className="small text-dark mb-1">
                                                <FeatherIconCom iconName="MapPin" size={13} className="me-1 text-secondary" />
                                                <strong>Beat/Territory:</strong> {item.beatName} | {item.territoryName}
                                            </div>
                                            <div className="small text-dark mb-3">
                                                <FeatherIconCom iconName="Calendar" size={13} className="me-1 text-secondary" />
                                                <strong>Last Captured:</strong> {item.lastSupportCaptureDate ? moment(item.lastSupportCaptureDate).format('DD MMM YYYY') : 'N/A'}
                                            </div>
                                        </div>

                                        <div>
                                            <div className="d-flex justify-content-between align-items-center p-2 rounded bg-light border mb-3 text-center">
                                                <div className="w-50 border-end">
                                                    <div className="text-muted small">Total Qty</div>
                                                    <div className="fw-bold text-dark">{item.totalQuantity}</div>
                                                </div>
                                                <div className="w-50">
                                                    <div className="text-muted small">Total Amount</div>
                                                    <div className="fw-bold text-dark">Rs. {Number(item.totalAmount || 0).toLocaleString('en-IN')}</div>
                                                </div>
                                            </div>

                                            <Button
                                                color="primary"
                                                outline
                                                size="sm"
                                                className="w-100 fw-bold"
                                                onClick={() => viewSupportDetails(item)}
                                            >
                                                View Support Items
                                            </Button>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        );
                    })}
                </Row>
            ) : (
                <div className="text-center py-5 border rounded bg-white">
                    <FeatherIconCom iconName="Inbox" size={48} className="mb-3 text-secondary" />
                    <h5 className="fw-bold text-dark">No Support Approvals Found</h5>
                    <p className="text-muted small">No support capture records found for the selected month and team member.</p>
                </div>
            )}

            {/* Support Items Detail Modal */}
            <Modal isOpen={detailModalOpen} toggle={() => setDetailModalOpen(!detailModalOpen)} centered size="lg">
                <div className="modal-header p-3 border-bottom d-flex justify-content-between align-items-center">
                    <h5 className="modal-title fw-bold text-dark m-0">Support Items - {selectedOutletName}</h5>
                    <Button color="link" className="p-0 text-dark border-0 shadow-none" onClick={() => setDetailModalOpen(false)}>
                        <FeatherIconCom iconName="X" size={20} />
                    </Button>
                </div>
                <ModalBody className="p-4" style={{ color: '#000000' }}>
                    {detailLoading ? (
                        <Loader height="150px" />
                    ) : detailItems.length > 0 ? (
                        <Table responsive bordered hover className="align-middle mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th className="fw-bold text-dark">Product Name</th>
                                    <th className="fw-bold text-dark">Quantity</th>
                                    <th className="fw-bold text-dark">Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {detailItems.map((prod: any, pIdx: number) => (
                                    <tr key={pIdx}>
                                        <td className="fw-semibold text-dark">
                                            {prod.Product?.ProductName || prod.ProductName || prod.product_name || 'N/A'}
                                        </td>
                                        <td className="fw-bold text-dark">{prod.Quantity ?? prod.qty ?? 0}</td>
                                        <td className="fw-bold text-dark">
                                            ₹{prod.TotalAmount ?? prod.Amount ?? prod.amount ?? 0}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    ) : (
                        <div className="text-center py-4 text-muted small">No support line items available</div>
                    )}
                </ModalBody>
            </Modal>
        </div>
    );
}
